import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  activeSection: string;
}

export default function Navbar({ activeSection }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const navItems = [
    { id: 'home', label: t('nav_home') },
    { id: 'services', label: t('nav_services') },
    { id: 'process', label: t('nav_process') },
    { id: 'case-studies', label: t('nav_references') },
    { id: 'about', label: t('nav_about') },
    { id: 'contact', label: t('nav_contact') }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center space-x-3 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/f6f2763d-db5f-4bb2-a284-c0c30ee4ca43.png"
              alt="Service Immobil Sicily"
              className="h-12 w-auto object-contain"
            />
          </button>

          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === item.id
                    ? 'text-[#1e3a8a]'
                    : isScrolled
                    ? 'text-gray-700 hover:text-[#1e3a8a]'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'en'
                    ? 'bg-[#1e3a8a] text-white'
                    : isScrolled
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('it')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'it'
                    ? 'bg-[#1e3a8a] text-white'
                    : isScrolled
                    ? 'text-gray-700 hover:bg-gray-100'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                IT
              </button>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="hidden md:block px-6 py-2.5 bg-[#1e3a8a] text-white text-sm font-medium rounded-lg hover:bg-[#1e40af] transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav_cta')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
